/**
 * Product Carousel
 *
 * Vertical thumbnail rail + main image + fullscreen viewer.
 * State (active index) is shared between the inline view and the
 * fullscreen dialog, so navigating in fullscreen updates the inline
 * view as well.
 */
class ProductCarousel extends HTMLElement {
  connectedCallback() {
    this.thumbs = Array.from(this.querySelectorAll('.product-carousel__thumb'));
    this.mainImages = Array.from(this.querySelectorAll('.product-carousel__main-image'));
    this.mainButton = this.querySelector('.product-carousel__main');
    this.dialog = this.querySelector('.product-carousel__fullscreen');
    this.fsImage = this.querySelector('.product-carousel__fs-image');
    this.index = 0;
    this._abort = new AbortController();

    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => this.setActive(Number(thumb.dataset.index)));
    });

    if (this.mainButton) {
      this.mainButton.addEventListener('click', () => this.openFullscreen());
    }

    if (this.dialog) {
      const prev = this.dialog.querySelector('[data-fs-prev]');
      const next = this.dialog.querySelector('[data-fs-next]');
      const close = this.dialog.querySelector('[data-fs-close]');

      if (prev) prev.addEventListener('click', () => this.step(-1));
      if (next) next.addEventListener('click', () => this.step(1));
      if (close) close.addEventListener('click', () => this.dialog.close());

      // Click on the backdrop area (the dialog element itself) closes.
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });

      // Arrow-key navigation while fullscreen. ESC close is native to <dialog>.
      this.dialog.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.step(-1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.step(1);
        }
      });
    }

    // Sync active image to the newly selected variant's featured_media
    // whenever Horizon's variant flow completes. Horizon 4.1.1 dispatches
    // `shopify:product:select` on the variant-picker (bubbles to the
    // enclosing shopify-section); event.promise resolves with the
    // fetched HTML and the new variant resource.
    const section = this.closest('.shopify-section') || document;
    section.addEventListener('shopify:product:select', this._onProductSelect.bind(this), {
      signal: this._abort.signal,
    });
  }

  disconnectedCallback() {
    this._abort?.abort();
  }

  _onProductSelect(event) {
    if (!event?.promise) return;
    event.promise
      .then((payload) => {
        const resource = payload?.detail?.resource || payload?.variant;
        const html = payload?.detail?.html;
        this._activateForVariant(resource, html);
      })
      .catch(() => {});
  }

  _activateForVariant(variant, html) {
    let targetIndex = -1;

    // Preferred: use the resource's featured_media id.
    const mediaId = variant?.featured_media?.id;
    if (mediaId != null) {
      targetIndex = this.thumbs.findIndex(
        (t) => t.dataset.mediaId === String(mediaId),
      );
    }

    // Fallback: match by CDN filename against the resource's preview.
    if (targetIndex === -1) {
      const previewSrc = variant?.featured_media?.preview_image?.src;
      if (previewSrc) {
        const targetBase = this._stripQuery(previewSrc);
        targetIndex = this.mainImages.findIndex(
          (img) => this._stripQuery(img.getAttribute('src') || '') === targetBase,
        );
      }
    }

    // Last resort: peek at the freshly rendered HTML for a thumb/main
    // image marked as active/aria-current and steal its media id.
    if (targetIndex === -1 && html) {
      const activeThumb = html.querySelector(
        '.product-carousel__thumb.is-active, .product-carousel__thumb[aria-current="true"]',
      );
      const activeMediaId = activeThumb?.getAttribute('data-media-id');
      if (activeMediaId) {
        targetIndex = this.thumbs.findIndex((t) => t.dataset.mediaId === activeMediaId);
      }
    }

    if (targetIndex >= 0 && targetIndex !== this.index) {
      this.setActive(targetIndex);
    }
  }

  _stripQuery(url) {
    if (!url) return '';
    // Also drop the transformation slug Shopify inserts before .ext
    // (e.g. _{width}x). Cheapest safe form: normalise to the pathname's
    // last segment without extension size suffixes.
    return url.split('?')[0].replace(/_\d+x\d*(?=\.)/, '');
  }

  get size() {
    return this.mainImages.length;
  }

  step(delta) {
    this.setActive(this.index + delta);
  }

  setActive(index) {
    if (!this.size) return;
    this.index = (index + this.size) % this.size;

    this.thumbs.forEach((thumb, i) => {
      const active = i === this.index;
      thumb.classList.toggle('is-active', active);
      if (active) {
        thumb.setAttribute('aria-current', 'true');
      } else {
        thumb.removeAttribute('aria-current');
      }
    });

    this.mainImages.forEach((image, i) => {
      image.classList.toggle('is-active', i === this.index);
    });

    const activeThumb = this.thumbs[this.index];
    if (activeThumb) {
      activeThumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    }

    if (this.dialog && this.dialog.open) {
      this.syncFullscreenImage();
    }
  }

  openFullscreen() {
    if (!this.dialog) return;
    this.syncFullscreenImage();
    this.dialog.showModal();
  }

  syncFullscreenImage() {
    const source = this.mainImages[this.index];
    if (!source || !this.fsImage) return;
    this.fsImage.src = source.getAttribute('src') || '';
    this.fsImage.srcset = source.getAttribute('srcset') || '';
    this.fsImage.alt = source.getAttribute('alt') || '';
  }
}

if (!customElements.get('product-carousel')) {
  customElements.define('product-carousel', ProductCarousel);
}
